// Typed env validation. Source of truth for which env vars the app needs.
// All vars are injected via `doppler run --` (see root package.json scripts).
// To add a new var: add it to Doppler, add it here with a Zod schema, use it
// via `import { env } from "@/lib/env"`.

import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

// Preview deploys run with prod-parity secrets (Vercel pulls the same
// Doppler stg/prd configs), so validation rules that protect against
// signature-verify fail-open — Inngest signing keys, DEV-flag gating —
// need to run there too. Local/CI is `VERCEL_ENV=undefined`, so stays
// lenient for dev ergonomics. See CLAUDE.md "### Env vars" framework.
const isProdOrPreview =
  process.env.VERCEL_ENV === "production" ||
  process.env.VERCEL_ENV === "preview";

export const env = createEnv({
  server: {
    // Supabase — required: needed for the chat handler + grader to read/write
    // the database. Local values come from `pnpm exec supabase status -o env`.
    SUPABASE_SECRET_KEY: z.string().min(1),
    // Must be the Supavisor transaction-mode pooler URL (port 6543), not a
    // direct connection (port 5432) — direct connections blow up under
    // subprocess concurrency in production (Vercel Functions + Inngest
    // workers + admin tools all connecting at once). CLAUDE.md gotcha #2.
    // Local dev runs against `pnpm exec supabase` (port :54322) where the
    // pooler isn't started by default and the concurrency pressure doesn't
    // exist, so the refine is prod/preview-only.
    SUPABASE_DB_URL: isProdOrPreview
      ? z
          .url()
          .refine(
            (u) => new URL(u).port === "6543",
            "SUPABASE_DB_URL must use the Supavisor transaction-mode pooler (port :6543), not the direct port :5432.",
          )
      : z.url(),

    // LLM providers. DeepSeek V4 is the chat default; Gemini Flash is the
    // grader-extractor default. Both are load-bearing — required across
    // all envs. Anthropic + OpenAI optional for future cross-model evals.
    //
    // Chat route uses Vercel AI Gateway (model-prefixed strings like
    // `deepseek/deepseek-v4-flash`). Gateway requires `AI_GATEWAY_API_KEY`
    // or OIDC token. With BYOK, the per-provider keys above are passed via
    // `providerOptions.gateway.byok` so the gateway authenticates as us
    // against DeepSeek/Google directly. Grader extractor stays on direct
    // `google()` factory — gold-set bench is tuned against that surface;
    // gateway swap would require re-baselining every sidecar.
    DEEPSEEK_API_KEY: z.string().min(1),
    GOOGLE_GENERATIVE_AI_API_KEY: z.string().min(1),
    // Required across all envs — local dev hits the SAME hosted gateway
    // as prod, so a missing key would fail every `/api/chat` request.
    // OIDC token auto-injection on Vercel deployments means we don't have
    // to manually set this in `prd` Doppler (Vercel injects), but env.ts
    // validates whatever ends up in process.env at boot — either source
    // is fine, both must produce a string.
    AI_GATEWAY_API_KEY: z.string().min(1),
    ANTHROPIC_API_KEY: z.string().min(1).optional(),
    OPENAI_API_KEY: z.string().min(1).optional(),

    // Deepgram — nova-3-medical ASR for the push-to-talk dictation feature
    // (POST /api/transcribe). Optional everywhere: when unset the route
    // returns 503 and the mic button surfaces "voice input isn't set up yet",
    // so the app still boots + runs without it. Set in Doppler dev + prd to
    // enable dictation.
    DEEPGRAM_API_KEY: z.string().min(1).optional(),
    // Authoring-time only: `pnpm roast:generate` synthesizes the creator roast
    // lines into committed mp3s. Production plays the files and never calls the
    // API, so these stay unset outside dev.
    ELEVENLABS_API_KEY: z.string().min(1).optional(),
    ELEVENLABS_ROAST_VOICE_ID: z.string().min(1).optional(),

    // Inngest (durable grader pipeline). Optional in dev/preview so the
    // Inngest dev server runs keyless; REQUIRED in production so a
    // misconfigured deploy fails at boot instead of silently fail-opening
    // on Inngest webhook signature verification.
    INNGEST_EVENT_KEY: isProdOrPreview
      ? z.string().min(1)
      : z.string().min(1).optional(),
    INNGEST_SIGNING_KEY: isProdOrPreview
      ? z.string().min(1)
      : z.string().min(1).optional(),
    // Must NOT be "1" in production or preview — that flag tells the
    // SDK to run in cloud-dev mode and bypass signature verification.
    INNGEST_DEV: isProdOrPreview
      ? z.string().refine((v) => v !== "1", {
          message: "INNGEST_DEV must not be 1 in production or preview",
        }).optional()
      : z.string().optional(),

    // Langfuse (LLM observability). The host is read by the @langfuse/otel
    // SDK directly from process.env.LANGFUSE_BASE_URL — not validated here
    // because nothing in app code consumes it via the env helper.
    LANGFUSE_PUBLIC_KEY: z.string().min(1).optional(),
    LANGFUSE_SECRET_KEY: z.string().min(1).optional(),

    // PostHog (feature flags + experiments). Optional everywhere — when
    // unset, `getExperimentVariant` falls back to hardcoded defaults so
    // gates never break on a missing key. The host defaults to
    // `https://us.i.posthog.com` in the client; override only if pointing
    // at the EU instance or a self-hosted deployment.
    POSTHOG_API_KEY: z.string().min(1).optional(),
    POSTHOG_HOST: z.url().optional(),

    // Person deletion on account delete (Privacy 1.3 §6 processor
    // propagation). `POSTHOG_API_KEY` above is a `phc_` INGESTION key and is
    // rejected by every `/api/projects/...` endpoint, so this needs its own
    // Personal API Key (`phx_...`, scope `person:write`). It is the ONLY
    // secret that surface needs — the private API host and the numeric
    // project id are constants in lib/privacy/posthog-person-delete.ts,
    // being neither secret nor per-environment.
    //
    // Optional in the SCHEMA only — local dev, previews and tests run without
    // it. On the production deploy an unset key FAILS LOUD; see
    // `unconfiguredProcessor` in lib/privacy/processor-deletion.ts.
    POSTHOG_PERSONAL_API_KEY: z.string().min(1).optional(),
    /** A SEPARATE, least-privilege key for the daily readout's PostHog read
     *  (`query:read` on this project only). Deliberately not the key above:
     *  that one carries the person-delete scope that
     *  lib/privacy/posthog-person-delete.ts needs for the GDPR deletion
     *  propagation, and swapping its value for a query-only key would break a
     *  legal obligation silently. Optional — unset falls back, and the readout
     *  degrades to "every signup unplaceable". */
    POSTHOG_QUERY_API_KEY: z.string().min(1).optional(),

    // Stripe (Layer 3 billing). Optional in dev so local Supabase + tests run
    // without provisioning Stripe; REQUIRED in production + preview so a
    // misconfigured deploy fails at boot instead of silently dropping
    // webhook signature verification (mirrors the Inngest pattern above).
    STRIPE_SECRET_KEY: isProdOrPreview
      ? z.string().min(1)
      : z.string().min(1).optional(),
    STRIPE_WEBHOOK_SECRET: isProdOrPreview
      ? z.string().min(1)
      : z.string().min(1).optional(),
    // Portal config for the premium monthly plan: no plan switching, so a $79
    // subscriber cannot move to the control catalog. Unset = Stripe's default.
    STRIPE_PORTAL_CONFIGURATION_PREMIUM: z.string().min(1).optional(),

    // Meta Conversions API (lib/analytics/meta-capi.ts). Optional everywhere:
    // unset, no server event is sent. The token comes from Events Manager →
    // the dataset → Settings → Conversions API. The test code (also from
    // Events Manager) routes events to its Test Events tab; leave it unset in
    // normal operation.
    META_CAPI_ACCESS_TOKEN: z.string().min(1).optional(),
    META_CAPI_TEST_EVENT_CODE: z.string().min(1).optional(),

    // App Store in-app purchases (StoreKit 2 in the iOS shell). All four are
    // OPTIONAL everywhere: they exist only once the App Store Connect API key
    // has been created (Users and Access → Integrations → In-App Purchase),
    // and the IAP surfaces degrade honestly without them — the verify action
    // and the V2 notifications route answer `not_configured` / 503 instead of
    // failing signature checks open. Tighten to prod-required once the key
    // is in Doppler `prd`. The bundle id is a code constant
    // (lib/iap/apple/config.ts), pinned to capacitor.config.ts by test.
    //   APPLE_IAP_ISSUER_ID   — the key's Issuer ID (a UUID)
    //   APPLE_IAP_KEY_ID      — the 10-character Key ID
    //   APPLE_IAP_PRIVATE_KEY — the .p8 contents (PEM; `\n`-escaped is fine)
    //   APPLE_APP_APPLE_ID    — the numeric App Store id of the app; REQUIRED
    //                           by Apple to verify PRODUCTION-signed data
    APPLE_IAP_ISSUER_ID: z.string().min(1).optional(),
    APPLE_IAP_KEY_ID: z.string().min(1).optional(),
    APPLE_IAP_PRIVATE_KEY: z.string().min(1).optional(),
    APPLE_APP_APPLE_ID: z
      .string()
      .regex(/^\d+$/, "APPLE_APP_APPLE_ID must be the numeric App Store id")
      .optional(),

    // Resend (transactional email). Template aliases are hardcoded in
    // lib/email.ts — they're identifiers, not secrets, and adding a new
    // lifecycle email shouldn't require a Doppler change.
    RESEND_API_KEY: isProdOrPreview
      ? z.string().min(1)
      : z.string().min(1).optional(),
    // From-address may be either plain `addr@host` or RFC 5322 mailbox
    // form `Display Name <addr@host>`. z.email() rejects the latter, so
    // we use min(1) here; Resend's API rejects malformed addresses at
    // send time.
    RESEND_FROM_TRANSACTIONAL: isProdOrPreview
      ? z.string().min(1)
      : z.string().min(1).optional(),

    // PoC access gate (replaced by real auth in Phase 1)
    POC_ACCESS_PASSWORD: z.string().min(1).optional(),

    // Premium-paywall A/B kill switch. Unset or `off`: control everywhere, no
    // cookies or assignments. Marketing reads it raw (a typo is off there).
    // Lifetimes already granted never depend on it.
    EXPERIMENT_PREMIUM_PAYWALL: z.enum(["on", "off"]).optional(),
  },
  client: {
    NEXT_PUBLIC_SUPABASE_URL: z.url(),
    NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY: z.string().min(1),
    // Marketing host for cross-host deep-links (e.g. Institutions card →
    // marketing /contact). Optional; read it as `MARKETING_URL` (bottom of
    // this file), which carries the prod default. Mirrors the
    // `NEXT_PUBLIC_APP_URL` pattern used in the reverse direction
    // from marketing → app.
    NEXT_PUBLIC_MARKETING_URL: z.url().optional(),
    // Origin of the retired V1 app (medceptor.com), where users' question-bank
    // progress still lives in browser localStorage. The recovery bridge embeds
    // a hidden iframe from this origin to read + postMessage that data into V2.
    // Optional — the bridge falls back to the prod default; override locally to
    // point at a V1 dev server for cross-origin testing. Temporary: removable
    // once V1 is decommissioned (~2026-06-24).
    NEXT_PUBLIC_V1_BRIDGE_ORIGIN: z.url().optional(),
    // Browser-side PostHog. The `phc_` project key is public by design
    // (it ships in the client bundle); same project as the server
    // `POSTHOG_API_KEY`. Optional so the app boots without it (session
    // replay just no-ops, mirroring the server `getPostHogClient` null
    // guard). The ingestion host is the `/medceptor-relay` reverse proxy
    // (next.config.js), so no public host var is needed.
    NEXT_PUBLIC_POSTHOG_KEY: z.string().min(1).optional(),
    // Dev-only escape hatch: set to "1" — on demand inline
    // (`env NEXT_PUBLIC_POSTHOG_FORCE=1 pnpm dev`) or in Doppler `dev_personal`
    // — to load the PostHog browser SDK under `next dev` (which otherwise never
    // inits — see instrumentation-client.ts) so client-capture widgets (NPS,
    // scenario + rubric feedback) render locally. Loads OPTED OUT of capturing,
    // so it never reaches the prod project; ignored entirely in prod builds.
    NEXT_PUBLIC_POSTHOG_FORCE: z.string().optional(),
    // Meta Pixel id, UNSET until the privacy policy that discloses it is live
    // (CLAUDE.md #45 a). No format check: `normalizePixelId` treats blank or
    // malformed as unset rather than failing boot.
    NEXT_PUBLIC_META_PIXEL_ID: z.string().optional(),
  },
  // Map process.env into runtime so Next.js can inline NEXT_PUBLIC_* vars.
  experimental__runtimeEnv: {
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_MARKETING_URL: process.env.NEXT_PUBLIC_MARKETING_URL,
    NEXT_PUBLIC_V1_BRIDGE_ORIGIN: process.env.NEXT_PUBLIC_V1_BRIDGE_ORIGIN,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_FORCE: process.env.NEXT_PUBLIC_POSTHOG_FORCE,
    NEXT_PUBLIC_META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID,
  },
  // Skip validation during typecheck/lint runs that don't have env loaded
  // (e.g. CI typecheck without Doppler context). Validation still runs at
  // runtime in dev/prod. Explicitly refuse to skip in production to
  // prevent a build where `SKIP_ENV_VALIDATION=1` leaked into CI masking
  // a real missing-var regression.
  skipValidation: process.env.SKIP_ENV_VALIDATION === "1" && !isProdOrPreview,
});

/** The marketing host for cross-host links; falls back to production. */
export const MARKETING_URL =
  env.NEXT_PUBLIC_MARKETING_URL ?? "https://medceptor.com";
